import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LogOut, Edit, Trash2, Plus, Heart, BarChart3, Clock, CheckCircle, AlertCircle, MapPin, Phone, ChevronDown, ChevronUp, Briefcase, Users, User, ChevronRight, Target, ShieldCheck, ShieldOff, Loader2, Shield, Bell, Ticket, Building, Eye, Package, TrendingUp, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import PreferenciasNotificacaoToggle from '../components/PreferenciasNotificacaoToggle';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import BusinessCard from '../components/BusinessCard';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import MinhasPromocoes from '../components/MinhasPromocoes';
import MinhasCampanhas from '../components/MinhasCampanhas';
import SearchTrendsWidget from '../components/SearchTrendsWidget';
import MinhasVagas from '../components/MinhasVagas';
import MinhasOrganizacoes from '../components/business/MinhasOrganizacoes';
import JornadaNegocio from '../components/JornadaNegocio';
import FeatureGate from '../components/FeatureGate';
import MinhaCarteiraCupons from '../components/MinhaCarteiraCupons';
import ValidarCupom from '../components/ValidarCupom';
import PendingTermsBanner from '../components/PendingTermsBanner';
import Breadcrumbs from '../components/Breadcrumbs';
import SEOHead from '../components/SEOHead';
import ImageUploader from '../components/ImageUploader';
import { resolveBackendImage } from '../utils/brandLogo';
import SegurancaSection from '../components/SegurancaSection';
import NotificationPreferencesSection from '../components/NotificationPreferencesSection';
import { calculateCompleteness, getCompletenessColor, getCompletenessTextColor, getCompletenessLabelText } from '../utils/businessCompleteness';
// Use centralized API service
import { 
  municipiosApi,
  favoritesApi,
  analyticsApi,
  businessesApi,
  authApi,
  profileApi 
} from '../services/api';

// Simple Form Input component for auth
const AuthFormInput = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-2">{label}</label>
    <input
      {...props}
      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
    />
  </div>
);

// Business Card for "Meus Negócios" with completeness and edit
// Ações incluídas (v33 — paridade com /meus-negocios antigo):
//   Ver · Editar · Delivery · Publicar no Feed · Meus posts · Gestores · Excluir
const ROLE_LABELS = {
  OWNER: { label: 'Proprietário', color: 'text-green-600', bg: 'bg-green-100' },
  REPRESENTATIVE: { label: 'Representante', color: 'text-blue-600', bg: 'bg-blue-100' },
  COLLABORATOR: { label: 'Colaborador', color: 'text-purple-600', bg: 'bg-purple-100' },
  ADMIN: { label: 'Admin', color: 'text-red-600', bg: 'bg-red-100' },
};

const STATUS_ICONS = {
  aprovado: { icon: CheckCircle, color: 'text-green-500', label: 'Aprovado' },
  pendente: { icon: Clock, color: 'text-yellow-500', label: 'Em Análise' },
  reprovado: { icon: XCircle, color: 'text-red-500', label: 'Reprovado' },
  bloqueado: { icon: XCircle, color: 'text-red-700', label: 'Bloqueado' },
  denunciado: { icon: AlertCircle, color: 'text-orange-500', label: 'Em análise' },
};

const MyBusinessCard = ({ business, onDelete, onEdit }) => {
  const completeness = calculateCompleteness(business);
  const lastUpdated = business.updated_at || business.created_at;

  const status = {
    text: getCompletenessLabelText(completeness),
    icon: completeness >= 80 ? CheckCircle : AlertCircle,
    color: getCompletenessTextColor(completeness),
  };
  const StatusIcon = status.icon;

  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden shadow-sm" data-testid={`my-business-card-${business.id}`}>
      <div className="flex flex-col md:flex-row">
        {/* Image — prefer new system imagens.logo, then galeria[0], then legacy avatar_url/images */}
        <div className="w-full md:w-48 h-36 md:h-auto bg-gray-100 relative flex-shrink-0">
          {(business.imagens?.logo?.medium || business.imagens?.logo?.thumb || business.avatar_url) ? (
            <img
              src={resolveBackendImage(business.imagens?.logo?.medium || business.imagens?.logo?.thumb || business.avatar_url)}
              alt={business.nome}
              className="w-full h-full object-cover"
            />
          ) : (business.imagens?.galeria?.[0]?.medium || business.imagens?.galeria?.[0]?.thumb) ? (
            <img
              src={resolveBackendImage(business.imagens.galeria[0].medium || business.imagens.galeria[0].thumb)}
              alt={business.nome}
              className="w-full h-full object-cover"
            />
          ) : business.images && business.images.length > 0 ? (
            <img src={resolveBackendImage(business.images[0])} alt={business.nome} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[var(--primary)]/10 to-[var(--primary)]/5">
              <MapPin className="w-12 h-12 text-[var(--primary)]/30" />
            </div>
          )}
          
          {/* Status Badge */}
          {business.verificado && (
            <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              Verificado
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            {/* Nome + badges de papel/status */}
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-bold text-lg">{business.nome}</h3>
              {(() => {
                const role = ROLE_LABELS[business.manager_role] || ROLE_LABELS.OWNER;
                return (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${role.bg} ${role.color}`}
                    data-testid={`business-role-${business.id}`}
                  >
                    {role.label}
                  </span>
                );
              })()}
              {(() => {
                const st = STATUS_ICONS[business.status_moderacao] || STATUS_ICONS.pendente;
                const StIcon = st.icon;
                return (
                  <span
                    className={`inline-flex items-center gap-1 text-xs ${st.color}`}
                    data-testid={`business-status-${business.id}`}
                  >
                    <StIcon className="w-3.5 h-3.5" />
                    {st.label}
                  </span>
                );
              })()}
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-3 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {business.bairro || 'Sem localização'}
            </p>

            {/* Completeness Bar */}
            <div className="max-w-xs">
              {completeness === 100 ? (
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200"
                  data-testid={`profile-100-complete-${business.id}`}
                >
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="font-semibold text-sm text-emerald-800">
                    Perfil 100% completo! ✅
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">Perfil</span>
                    <span className={`flex items-center gap-1 ${status.color}`}>
                      <StatusIcon className="w-4 h-4" />
                      {completeness}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getCompletenessColor(completeness)} transition-all duration-500`}
                      style={{ width: `${completeness}%` }}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Last Updated */}
            <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mt-2">
              <Clock className="w-3.5 h-3.5" />
              Atualizado: {lastUpdated ? new Date(lastUpdated).toLocaleDateString('pt-BR') : 'N/A'}
            </div>

            {/* Preferências de notificação (apenas OWNER/REPRESENTATIVE) */}
            {(business.manager_role === 'OWNER' || business.manager_role === 'REPRESENTATIVE') && (
              <div className="mt-3">
                <PreferenciasNotificacaoToggle
                  negocioId={business.id}
                  categoria={business.categoria_nome || business.categoria}
                  initialValue={business?.preferencias_notificacao?.receber_solicitacoes !== false}
                />
              </div>
            )}
          </div>

          {/* Actions — paridade com /meus-negocios (v33): Ver, Editar, Delivery, Feed novo, Meus posts, Gestores, Excluir */}
          <div className="flex flex-wrap md:flex-col gap-2 md:w-48">
            <Link
              to={`/negocio/${business.id}`}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
              data-testid={`view-business-${business.id}`}
            >
              <Eye className="w-4 h-4" />
              Ver
            </Link>

            {(business.manager_role === 'OWNER' || business.manager_role === 'REPRESENTATIVE') && (
              <button
                onClick={() => onEdit(business)}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-[var(--primary)] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[var(--primary)]/90 transition-all"
                data-testid={`edit-business-${business.id}`}
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
            )}

            {(business.manager_role === 'OWNER' || business.manager_role === 'REPRESENTATIVE') && business.municipio_delivery_enabled !== false && (
              <Link
                to={`/empreendedor/delivery/${business.id}`}
                className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  business.delivery_ativo
                    ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-sm font-semibold'
                    : 'border border-orange-200 text-orange-700 hover:bg-orange-50'
                }`}
                data-testid={`delivery-business-${business.id}`}
                title={business.delivery_ativo ? 'Painel Delivery (ativo)' : 'Ativar Delivery'}
              >
                <Package className="w-4 h-4" />
                {business.delivery_ativo ? 'Delivery ativo' : 'Delivery'}
              </Link>
            )}

            {(business.manager_role === 'OWNER' || business.manager_role === 'REPRESENTATIVE') && (
              <Link
                to={`/empreendedor/feed/novo?negocio_id=${encodeURIComponent(business.id)}`}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
                data-testid={`feed-publicar-${business.id}`}
                title="Publicar no Feed"
              >
                <Bell className="w-4 h-4" />
                Publicar no Feed
              </Link>
            )}

            {(business.manager_role === 'OWNER' || business.manager_role === 'REPRESENTATIVE') && (
              <Link
                to={`/empreendedor/feed/posts?negocio_id=${encodeURIComponent(business.id)}`}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
                data-testid={`feed-meus-${business.id}`}
                title="Meus posts no Feed"
              >
                <TrendingUp className="w-4 h-4" />
                Meus posts
              </Link>
            )}

            {business.manager_role === 'OWNER' && (
              <>
                <Link
                  to={`/negocio/${business.id}/gestores`}
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
                  data-testid={`managers-business-${business.id}`}
                >
                  <Users className="w-4 h-4" />
                  Gestores
                </Link>
                <button
                  onClick={() => onDelete(business.id)}
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  data-testid={`delete-business-${business.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Perfil() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, logout, login: authLogin, refreshUser } = useAuth();
  const { location } = useLocation();
  
  const [businesses, setBusinesses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const VALID_TABS = ['negocios', 'favoritos', 'cupons', 'vagas', 'social', 'analytics', 'seguranca', 'notificacoes'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'negocios';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Mantém a URL sincronizada com a aba ativa (?tab=cupons) — permite que
  // o menu "Meus cupons" (Header) leve direto para a aba certa via /perfil?tab=cupons.
  // Preserva `new` e `negocio_id` (usados pelos CTAs de /negocio/... para auto-abrir modais).
  useEffect(() => {
    const preserved = {};
    ['new', 'negocio_id'].forEach(k => {
      const v = searchParams.get(k);
      if (v) preserved[k] = v;
    });
    if (activeTab !== 'negocios') {
      setSearchParams({ tab: activeTab, ...preserved }, { replace: true });
    } else if (searchParams.get('tab')) {
      setSearchParams(preserved, { replace: true });
    }
  }, [activeTab, setSearchParams, searchParams]);

  // Sincroniza a aba com a querystring quando ela muda POR FORA
  // (ex.: clique em "Meus favoritos"/"Meus cupons" no Header enquanto o
  // usuário já está em /perfil). Sem isso, a URL muda mas o componente
  // fica preso na aba antiga porque `initialTab` só roda na montagem.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const nextTab = VALID_TABS.includes(urlTab) ? urlTab : 'negocios';
    if (nextTab !== activeTab) setActiveTab(nextTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [municipio, setMunicipio] = useState(null);
  const [empreendedorPerfil, setEmpreendedorPerfil] = useState(null);
  const [showFotoEdit, setShowFotoEdit] = useState(false);
  
  // Auth state
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    nome: '',
    telefone: '',
    cpf: ''
  });

  // Load empreendedor perfil
  const loadEmpreendedorPerfil = useCallback(async () => {
    try {
      const data = await profileApi.getEmpreendedor();
      setEmpreendedorPerfil(data);
    } catch (e) {
      console.error('Error loading empreendedor perfil:', e);
    }
  }, []);

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const allMunicipios = await municipiosApi.list();
          const found = allMunicipios.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  useEffect(() => {
    if (!user) {
      // Don't redirect immediately, let the auth context check if there's a stored token
      return;
    }
    loadBusinesses();
    loadFavorites();
    loadAnalytics();
    loadEmpreendedorPerfil();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadEmpreendedorPerfil]);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const data = await profileApi.getBusinesses();
      setBusinesses(data);
    } catch (error) {
      console.error('Erro ao carregar negócios:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const lat = location?.latitude;
      const lon = location?.longitude;
      const data = await favoritesApi.list(lat, lon);
      setFavorites(data);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await analyticsApi.dashboard();
      setAnalytics(data);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm('Tem certeza que deseja deletar este negócio?')) {
      return;
    }

    try {
      await businessesApi.delete(businessId);
      setBusinesses(businesses.filter(b => b.id !== businessId));
      alert('Negócio deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar negócio:', error);
      alert('Erro ao deletar negócio');
    }
  };

  // Auth submit handler
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      const data = isLogin 
        ? { email: authForm.email, password: authForm.password }
        : { ...authForm };
      
      const res = isLogin
        ? await authApi.login(data)
        : await authApi.register(data);
      // IMPORTANTE: passar refresh_token, senão o AuthContext.login() apaga
      // qualquer refresh anterior e o próximo 401 dispara auto-logout com
      // "Sua sessão expirou". Bug reportado em prod (iter 219).
      authLogin(res.access_token, res.user, res.refresh_token || null);
    } catch (error) {
      console.error('Erro de autenticação:', error);
      alert(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setAuthLoading(false);
    }
  };

  // Show login form if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--background)] pb-32 pt-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
              {isLogin ? 'Entrar' : 'Criar conta'}
            </h1>
            <p className="text-[var(--text-secondary)] mb-6">
              {isLogin ? 'Entre para ver seu perfil' : 'Crie uma conta para começar'}
            </p>

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              <AuthFormInput
                label="Email"
                type="email"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                required
                data-testid="auth-email-input"
              />

              <AuthFormInput
                label="Senha"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                required
                data-testid="auth-password-input"
              />

              {!isLogin && (
                <>
                  <AuthFormInput
                    label="Nome completo"
                    type="text"
                    value={authForm.nome}
                    onChange={(e) => setAuthForm({ ...authForm, nome: e.target.value })}
                    required
                  />
                  <AuthFormInput
                    label="Telefone"
                    type="tel"
                    value={authForm.telefone}
                    onChange={(e) => setAuthForm({ ...authForm, telefone: e.target.value })}
                    required
                  />
                  <AuthFormInput
                    label="CPF"
                    type="text"
                    value={authForm.cpf}
                    onChange={(e) => setAuthForm({ ...authForm, cpf: e.target.value })}
                    required
                  />
                </>
              )}

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-[var(--primary)] text-white py-3 rounded-xl font-semibold hover:bg-[var(--primary)]/90 transition-all disabled:opacity-50"
                data-testid="auth-submit-btn"
              >
                {authLoading ? 'Carregando...' : (isLogin ? 'Entrar' : 'Criar conta')}
              </button>

              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="w-full text-center text-sm text-[var(--primary)] hover:underline"
              >
                {isLogin ? 'Não tem conta? Criar conta' : 'Já tem conta? Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead title="Meu Perfil" noindex={true} />
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Breadcrumbs 
            items={[
              { label: 'Meu Perfil', path: null }
            ]}
            municipio={municipio}
          />
        </div>
      </div>
      
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-wrap items-start justify-between gap-y-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {/* Avatar — prefers new system foto.thumb, fallback to legacy avatar_url */}
              <button
                type="button"
                onClick={() => setShowFotoEdit((v) => !v)}
                className="relative w-16 h-16 flex-shrink-0 rounded-full overflow-hidden border-2 border-purple-200 bg-purple-100 hover:ring-2 hover:ring-purple-400 transition-all group"
                title="Alterar foto"
                data-testid="profile-photo-toggle"
              >
                {(user.foto?.medium || user.foto?.thumb || user.avatar_url) ? (
                  <img
                    src={resolveBackendImage(user.foto?.medium || user.foto?.thumb || user.avatar_url)}
                    alt={user.nome}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-8 h-8 text-purple-600" />
                  </div>
                )}
                <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-medium">
                  Editar
                </span>
              </button>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold mb-1 truncate" style={{ fontFamily: 'Manrope' }}>
                  {user.nome}
                </h1>
                <p className="text-[var(--text-secondary)] truncate">{user.email}</p>
                <p className="text-sm text-[var(--text-muted)] mt-1 truncate">{user.telefone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/conta/privacidade')}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-[var(--text-secondary)] hover:text-emerald-600 transition-colors"
                data-testid="account-privacy-link"
                title="Minha privacidade (LGPD)"
              >
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium hidden sm:inline">Privacidade</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-[var(--text-secondary)] hover:text-red-600 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
          {showFotoEdit && (
            <div className="mt-6 max-w-md" data-testid="profile-photo-uploader">
              <ImageUploader
                area="perfil"
                entidadeId={user.id}
                municipioId={user.municipio_id}
                imagensAtuais={
                  user.foto
                    ? user.foto
                    : (user.avatar_url
                        ? { thumb: user.avatar_url, medium: user.avatar_url, large: user.avatar_url, legacy: true }
                        : null)
                }
                single
                label="Foto de perfil"
                description="Imagem quadrada (1:1). Será exibida em comentários e avaliações."
                onChange={async (imgs) => {
                  // Refresh auth user from server to get fresh foto/avatar
                  try {
                    await refreshUser?.();
                  } catch (_) { /* noop */ }
                  if (imgs.length === 0) {
                    setShowFotoEdit(false);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Card Complete seu Perfil */}
        {empreendedorPerfil && empreendedorPerfil.percentual_preenchido < 100 && (
          <div 
            onClick={() => navigate('/complete-seu-perfil')}
            className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all"
            data-testid="complete-perfil-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Complete seu Perfil</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Aumente sua visibilidade e ajude a fortalecer ações de apoio ao empreendedor no município.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                      style={{ width: `${empreendedorPerfil.percentual_preenchido}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-purple-700">
                    {empreendedorPerfil.percentual_preenchido}%
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        )}
        
        {empreendedorPerfil && empreendedorPerfil.percentual_preenchido === 100 && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <span className="font-medium text-green-800">Perfil 100% completo!</span>
                <button 
                  onClick={() => navigate('/complete-seu-perfil')}
                  className="ml-2 text-sm text-green-600 hover:underline"
                >
                  Editar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-[var(--border)] mb-6 -mx-4 px-4">
          <div
            className="flex items-center gap-4 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid="perfil-tabs-scroll"
          >
          <button
            onClick={() => setActiveTab('negocios')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex-shrink-0 whitespace-nowrap ${
              activeTab === 'negocios'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-negocios"
          >
            Meus Negócios
            {activeTab === 'negocios' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('favoritos')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex items-center gap-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === 'favoritos'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-favoritos"
          >
            <Heart className="w-4 h-4" />
            Favoritos
            {activeTab === 'favoritos' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('cupons')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex items-center gap-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === 'cupons'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-cupons"
          >
            <Ticket className="w-4 h-4" />
            Cupons
            {activeTab === 'cupons' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('vagas')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex items-center gap-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === 'vagas'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-vagas"
          >
            <Briefcase className="w-4 h-4" />
            Vagas
            {activeTab === 'vagas' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('social')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex items-center gap-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === 'social'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-social"
          >
            <Users className="w-4 h-4" />
            Social
            {activeTab === 'social' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-2 font-semibold transition-colors relative flex items-center gap-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === 'analytics'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
            {activeTab === 'analytics' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('seguranca')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors relative flex-shrink-0 whitespace-nowrap ${
              activeTab === 'seguranca'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-seguranca"
          >
            <ShieldCheck className="w-4 h-4" />
            Segurança
            {activeTab === 'seguranca' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('notificacoes')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors relative flex-shrink-0 whitespace-nowrap ${
              activeTab === 'notificacoes'
                ? 'text-[var(--primary)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--primary)]'
            }`}
            data-testid="tab-notificacoes"
          >
            <Bell className="w-4 h-4" />
            Notificações
            {activeTab === 'notificacoes' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]" />
            )}
          </button>
          </div>
        </div>

        {/* Segurança Tab */}
        {activeTab === 'seguranca' && (
          <SegurancaSection user={user} refreshUser={refreshUser} />
        )}

        {/* Notificações Tab */}
        {activeTab === 'notificacoes' && (
          <NotificationPreferencesSection user={user} refreshUser={refreshUser} />
        )}

        {/* Vagas Tab */}
        {activeTab === 'vagas' && (
          <MinhasVagas />
        )}

        {/* Social Tab */}
        {activeTab === 'social' && (
          <MinhasOrganizacoes />
        )}

        {/* Meus Negócios Tab */}
        {activeTab === 'negocios' && (
          <div>
            <PendingTermsBanner />
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Meus negócios
              </h2>
              <button
                onClick={() => navigate('/cadastrar')}
                className="flex items-center gap-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] rounded-full px-6 py-2.5 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
                data-testid="add-business-button"
              >
                <Plus className="w-4 h-4" />
                Adicionar negócio
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : businesses.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-[#F3E8FF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-[var(--primary)]" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                  Nenhum negócio cadastrado
                </h3>
                <p className="text-[var(--text-secondary)] mb-6">
                  Cadastre seu primeiro negócio e comece a aparecer para sua comunidade
                </p>
                <button
                  onClick={() => navigate('/cadastrar')}
                  className="bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-8 py-3 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
                >
                  Cadastrar meu negócio
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* DRAFTS — "Continue de onde parou" (movido de /meus-negocios v32) */}
                {(() => {
                  const drafts = businesses.filter((b) => b?.is_draft === true);
                  const completos = businesses.filter((b) => b?.is_draft !== true);
                  return (
                    <>
                      {drafts.length > 0 && (
                        <div
                          className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl p-4 sm:p-6"
                          data-testid="drafts-section"
                        >
                          <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                              <AlertCircle className="w-5 h-5 text-amber-700" />
                            </div>
                            <div>
                              <h3 className="font-bold text-amber-900">
                                Cadastros incompletos ({drafts.length})
                              </h3>
                              <p className="text-sm text-amber-800">
                                Você começou esses cadastros mas ainda não concluiu. Termine para que apareçam publicamente.
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {drafts.map((d) => (
                              <div
                                key={d.id}
                                className="bg-white border border-amber-200 rounded-lg p-3 flex items-center gap-3 hover:shadow-sm transition-shadow"
                                data-testid={`draft-card-${d.id}`}
                              >
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  {d.imagens?.logo?.thumb ? (
                                    <img
                                      src={resolveBackendImage(d.imagens.logo.thumb)}
                                      alt={d.nome}
                                      className="w-full h-full rounded-lg object-cover object-center block"
                                    />
                                  ) : (
                                    <Building className="w-5 h-5 text-amber-700" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-sm truncate">{d.nome || 'Sem nome'}</p>
                                  <p className="text-xs text-amber-800">
                                    {d.categoria || 'Categoria não informada'} · iniciado em{' '}
                                    {new Date(d.created_at).toLocaleDateString('pt-BR')}
                                  </p>
                                </div>
                                <Link
                                  to={`/cadastrar?continue=${d.id}`}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors"
                                  data-testid={`continue-draft-${d.id}`}
                                >
                                  Concluir cadastro
                                  <ChevronRight className="w-4 h-4" />
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {completos.map(business => (
                  <div key={business.id} className="space-y-4">
                    {/* Business Card */}
                    <MyBusinessCard
                      business={business}
                      onDelete={handleDelete}
                      onEdit={(biz) => navigate(`/editar-negocio/${biz.id}`)}
                    />
                    
                    {/* Jornada de Evolução do Negócio */}
                    <JornadaNegocio businessId={business.id} />
                    
                    {/* Search Trends Widget */}
                    <FeatureGate businessId={business.id} feature="search_trends">
                      <SearchTrendsWidget businessId={business.id} />
                    </FeatureGate>
                    
                    {/* Promotions Section */}
                    <FeatureGate businessId={business.id} feature="promotions">
                      <MinhasPromocoes business={business} />
                    </FeatureGate>

                    {/* Validar Cupom (Lojista) */}
                    <ValidarCupom business={business} />
                  </div>
                ))}
                    </>
                  );
                })()}

                {/* Campanhas (orquestrador municipal — uma vez) */}
                <div data-testid="perfil-secao-campanhas" className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    Campanhas do município
                  </h3>
                  <MinhasCampanhas />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Favoritos Tab */}
        {activeTab === 'favoritos' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>
              Meus favoritos
            </h2>
            {favorites.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                  Nenhum favorito ainda
                </h3>
                <p className="text-[var(--text-secondary)] mb-6">
                  Salve seus negócios favoritos para acessá-los rapidamente
                </p>
                <button
                  onClick={() => navigate('/explorar')}
                  className="bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-8 py-3 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
                >
                  Explorar negócios
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favorites.map(business => (
                  <BusinessCard key={business.id} business={business} showDistance={true} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cupons Tab */}
        {activeTab === 'cupons' && (
          <div className="space-y-6" data-testid="cupons-tab-content">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Ticket className="w-5 h-5 text-[var(--primary)]" />
              Meus cupons
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Cupons resgatados e disponíveis para uso nos negócios do seu município.
            </p>
            <MinhaCarteiraCupons />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}
      </div>
    </div>
  );
}
